/**
 * Builds flat list data for Arena FlashList: one row per section (header + cards inside one container).
 * Each section is a single list item so the design shows one card container per section.
 *
 * All tab: active/pending sections + a 5-item finished preview strip (fetched in parallel on load).
 * Finished tab: full paginated history (lazy-loaded when user opens the tab).
 */

import type { ChallengeWithStats } from '../../../types/challenges';
import type { ChallengeSectionBuckets } from '../types/challenges.types';
import type { ArenaListItem, FilterType } from '../types/challenges.types';
import { SECTION_TITLES } from '../constants/challenges.constants';

const NON_FINISHED_BUCKETS: (keyof ChallengeSectionBuckets)[] = [
  'yourMove',
  'live',
  'active',
  'waitingForFriends',
  'waitingForTaker',
  'waitingForOpponent',
];

function bucketsForFilter(activeFilter: FilterType): (keyof ChallengeSectionBuckets)[] {
  if (activeFilter === 'active') return NON_FINISHED_BUCKETS;
  if (activeFilter === 'ready') return ['yourMove', 'active'];
  // "Pending" groups the awaiting states: invited friends + open challenges awaiting a taker.
  if (activeFilter === 'pending') return ['waitingForFriends', 'waitingForTaker'];
  if (activeFilter === 'finished') return [];
  return [];
}

interface BuildArenaFlatListOptions {
  sectionBuckets: ChallengeSectionBuckets;
  historyChallenges: ChallengeWithStats[];
  /** Up to 5 finished challenges fetched in parallel on page load. Shown as preview strip in All tab. */
  historyPreview: ChallengeWithStats[];
  isLoadingHistoryPreview: boolean;
  hasNextPage: boolean;
  activeFilter: FilterType;
  isLoadingHistory: boolean;
  /** When false, do not add the preview strip so the list doesn't show it until the page has loaded from skeleton. */
  isInitialLoadComplete: boolean;
}

export function buildArenaFlatList(opts: BuildArenaFlatListOptions): ArenaListItem[] {
  const list: ArenaListItem[] = [];

  const bucketsToShow = bucketsForFilter(opts.activeFilter);
  for (const bucket of bucketsToShow) {
    if (bucket === 'finished') continue;
    const challenges = opts.sectionBuckets[bucket] ?? [];
    if (challenges.length === 0) continue;
    list.push({
      type: 'section',
      sectionBucket: bucket,
      title: SECTION_TITLES[bucket],
      challenges,
    });
  }

  // All tab: show finished preview strip once initial load is done.
  // Shows skeleton while preview is loading, preview cards when ready, nothing when history is empty.
  if (opts.activeFilter === 'active' && opts.isInitialLoadComplete) {
    if (opts.isLoadingHistoryPreview && opts.historyPreview.length === 0) {
      list.push({ type: 'finishedSkeleton' });
    } else if (opts.historyPreview.length > 0) {
      list.push({
        type: 'historyPreview',
        challenges: opts.historyPreview,
      });
    }
    // If preview is empty and not loading: no finished history exists — show nothing.
  }

  // Finished tab: show skeleton while loading or section when we have data.
  if (opts.activeFilter === 'finished') {
    if (opts.isLoadingHistory && opts.historyChallenges.length === 0) {
      list.push({ type: 'finishedSkeleton' });
    } else if (opts.historyChallenges.length > 0) {
      list.push({
        type: 'section',
        sectionBucket: 'finished',
        title: SECTION_TITLES.finished,
        challenges: opts.historyChallenges,
      });
    }
  }

  return list;
}

/** Row index for a challenge id (`section` or All-tab `historyPreview`). */
export function findArenaListRowIndexForChallengeId(
  listData: ArenaListItem[],
  challengeId: string,
): number {
  return listData.findIndex(
    (item) =>
      (item.type === 'section' || item.type === 'historyPreview') &&
      item.challenges.some((c) => c.id === challengeId),
  );
}

/**
 * Indices of section rows for sticky headers. Excludes the last section so scrolling
 * to the bottom doesn't get stuck (last section + loadMore would otherwise fight sticky).
 */
export function getStickyHeaderIndices(listData: ArenaListItem[]): number[] {
  const indices = listData
    .map((item, index) => (item.type === 'section' ? index : -1))
    .filter((i) => i >= 0);
  if (indices.length <= 1) return indices;
  return indices.slice(0, -1);
}
